import { useRef, type KeyboardEvent, type MouseEvent, type ReactElement, type Ref } from "react";
import { classes } from "./classes.ts";
import { Glyph } from "./Glyph.tsx";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";
import { fieldFrame, frameClass } from "./state.ts";

/**
 * The text field's search form (the design's `TextField` with `type="search"`; ADR-125 decision 4.2): the field's
 * framed box with no label line above it, the 12 px magnifier at its left, the input with its placeholder, then,
 * when the page gives one, a count at its right in the pixel face (the muted colour, the danger colour when
 * `none` says nothing matches), a polite live region so a screen reader hears it change, and the clear cross, shown
 * while the field holds text. Its accessible name is its `label`, since no label line is drawn. A focused field draws
 * the aether focus frame, and the input router gives it the keyboard (CLIENT.md 7.3), so every key types.
 *
 * The form answers its own keys before the input router hears them: Escape clears the text; Escape in an empty field,
 * and Enter, call `onExit`, where the page moves the keyboard out of the field. Each key it answers is prevented, so
 * the router leaves it alone; with no `onExit`, Escape in an empty field and Enter are left to the router. A key an
 * input method is composing with is never answered. The cross clears the text. A pointer press on it leaves the
 * keyboard's focus where it was, so it never takes the keyboard from the game; pressed from the keyboard, with focus
 * on the cross, which goes as the text does, it hands focus back to the input.
 */

/**
 * What the search form does with a key: clear its text, hand the keyboard out of the field, or nothing, leaving the
 * key to the input and the router.
 */
export type SearchKeyAction = "clear" | "exit" | null;

/**
 * The search form's answer to a key, in its order: an auto-repeat and a key an input method is composing with get
 * none; Escape clears the text while there is any; Escape in an empty field and Enter hand the keyboard out when the
 * page says where (`exits`); anything else is the input's.
 *
 * @param key The key's name.
 * @param value The field's text.
 * @param exits Whether the page takes the keyboard out of the field.
 * @param held Whether the key is an auto-repeat or part of a composition.
 * @returns The answer.
 * @example
 * searchKeyAction("Escape", "pot", true, false);
 * // "clear"
 */
export function searchKeyAction(key: string, value: string, exits: boolean, held: boolean): SearchKeyAction {
  if (held) {
    return null;
  }
  if (key === "Escape" && value !== "") {
    return "clear";
  }
  return (key === "Escape" || key === "Enter") && exits ? "exit" : null;
}

/**
 * The count drawn inside the field: its text and whether it says nothing matches.
 */
export interface SearchCount {
  readonly text: string;
  readonly none: boolean;
}

/**
 * Keeps the keyboard focus where it is when the pointer presses the clear cross.
 *
 * @param event The mouse press.
 */
function keepFocus(event: MouseEvent<HTMLButtonElement>): void {
  event.preventDefault();
}

/**
 * Whether a key belongs to an input method's composition, which the field leaves to the input method.
 *
 * @param event The key.
 * @returns True while composing.
 */
function composing(event: KeyboardEvent<HTMLInputElement>): boolean {
  return event.nativeEvent.isComposing || event.keyCode === 229;
}

/**
 * The clear cross: a link-styled button holding the 12 px cross.
 *
 * @param props Its accessible `label` and what a press does (`onClear`), told whether the cross had the keyboard's
 *   focus as it was pressed.
 * @returns The button.
 */
function ClearButton({ label, onClear }: { readonly label: string; readonly onClear: (focused: boolean) => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled: false, focus: "visible" });
  const box = KIT_METRICS.field.reveal;
  return (
    <button type="button" className={classes("ot-link", "ot-field__reveal", "ot-field__clear", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus")} data-kit="field-clear" aria-label={label} onClick={(event) => onClear(event.currentTarget === document.activeElement)} onMouseDown={keepFocus} style={{ width: logical(box.box), height: logical(box.box), padding: logical(box.glyphOffset), marginRight: logical(box.right) }} {...handlers}>
      <Glyph name="close" />
    </button>
  );
}

/**
 * A search field.
 *
 * @param props Its accessible `label`; its `placeholder`; its `value` and `onChange`; `maxLength`, which stops the
 *   typing; the `count` drawn at its right, or null for none; the clear cross's accessible name (`clearLabel`);
 *   `onExit`, called on Escape in an empty field and on Enter, absent where the router takes both; and `inputRef`.
 * @returns The field.
 * @example
 * <SearchField label="Search items" placeholder="Search items" value={query} onChange={setQuery} maxLength={32} count={{ text: "2/7", none: false }} clearLabel="Clear search" onExit={leave} />
 */
export function SearchField({ label, placeholder, value, onChange, maxLength, count, clearLabel, onExit, inputRef }: { readonly label: string; readonly placeholder: string; readonly value: string; readonly onChange: (value: string) => void; readonly maxLength?: number; readonly count: SearchCount | null; readonly clearLabel: string; readonly onExit?: () => void; readonly inputRef?: Ref<HTMLInputElement> }): ReactElement {
  const { state, handlers } = useInteraction({ disabled: false, focus: "any" });
  const input = useRef<HTMLInputElement | null>(null);
  const frame = fieldFrame({ ...state, disabled: false, error: false });
  const metrics = KIT_METRICS.field;

  /**
   * Answers Escape and Enter as the form's own keys.
   *
   * @param event The key.
   */
  const keyed = (event: KeyboardEvent<HTMLInputElement>): void => {
    const action = searchKeyAction(event.key, value, onExit !== undefined, event.repeat || composing(event));
    if (action === "clear") {
      event.preventDefault();
      onChange("");
    } else if (action === "exit") {
      event.preventDefault();
      onExit?.();
    }
  };

  /**
   * Holds the input for the cross and hands it to the page's ref too.
   *
   * @param element The input, or null as it goes.
   */
  const refer = (element: HTMLInputElement | null): void => {
    input.current = element;
    if (typeof inputRef === "function") {
      inputRef(element);
    } else if (inputRef !== undefined && inputRef !== null) {
      (inputRef as { current: HTMLInputElement | null }).current = element;
    }
  };

  return (
    <div className={classes("ot-field", "ot-field--search", state.focus && "is-focus")} data-kit="search-field">
      <div className={classes("ot-field__box", "ot-field__box--search", frameClass(frame))} data-kit="field-box" style={{ height: logical(metrics.boxHeight), paddingLeft: logical(metrics.paddingX), columnGap: logical(metrics.search.gap) }} onPointerEnter={handlers.onPointerEnter} onPointerLeave={handlers.onPointerLeave} onPointerCancel={handlers.onPointerCancel}>
        <span className="ot-field__magnifier" aria-hidden="true">
          <Glyph name="search" />
        </span>
        <input
          ref={refer}
          className="ot-field__input"
          type="search"
          value={value}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-label={label}
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => onChange(event.currentTarget.value)}
          onKeyDown={keyed}
          onFocus={handlers.onFocus}
          onBlur={handlers.onBlur}
          style={{ padding: `${logical(metrics.paddingY)} 0` }}
        />
        <span className={classes("ot-field__found", count?.none === true && "is-none")} data-kit="field-found" aria-live="polite" style={value === "" ? { marginRight: logical(metrics.paddingX) } : undefined}>
          {count?.text ?? ""}
        </span>
        {value === "" ? null : <ClearButton
            label={clearLabel}
            onClear={(focused) => {
              onChange("");
              if (focused) {
                input.current?.focus();
              }
            }}
          />}
      </div>
    </div>
  );
}
